// Sortable table component for block and widget renderers.

import React, { useMemo, useState } from 'react';
import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon } from 'lucide-react';
import type { RichTextNode } from '@airhead-games/shared/content';
import RichTextRenderer from '@/content/rendering/renderers/RichTextRenderer';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/utilities/cn';
import { applySortDirection, type SortDirection } from '@/utilities/sort';

interface SortableTableProps {
  columns: RichTextNode[][];
  rows: RichTextNode[][][];
  variant?: 'default' | 'report';
}

type SortState = {
  columnIndex: number;
  direction: SortDirection;
};

type ReportColumnRole = 'rank' | 'subject' | 'stat';

const REPORT_RANK_COLUMN_WIDTH_REM = 4.75;
const REPORT_STAT_COLUMN_WIDTH_REM = 8.25;
const REPORT_SUBJECT_MIN_WIDTH_REM = 14;

const plainText = (nodes: RichTextNode[] = []): string =>
  nodes
    .map((node) => {
      if (node.type === 'text') return node.text;
      return plainText(node.children);
    })
    .join('');

const isRankColumn = (nodes: RichTextNode[] | undefined): boolean =>
  plainText(nodes).trim().toLocaleLowerCase() === 'rank';

const valueForSort = (nodes: RichTextNode[]): string | number => {
  const text = plainText(nodes).trim();
  const normalizedNumber = text
    .replace(/,/g, '')
    .replace(/%$/, '')
    .replace(/^~$/, '');
  const parsed = Number(normalizedNumber);

  if (text && !Number.isNaN(parsed)) {
    return parsed;
  }

  const parsedDate = Date.parse(text);
  if (!Number.isNaN(parsedDate)) {
    return parsedDate;
  }

  return text.toLocaleLowerCase();
};

const compareValues = (
  a: string | number,
  b: string | number,
  direction: SortDirection
) => {
  const result =
    typeof a === 'number' && typeof b === 'number'
      ? a - b
      : String(a).localeCompare(String(b), undefined, {
          numeric: true,
          sensitivity: 'base',
        });

  return applySortDirection(result, direction);
};

const getReportColumnRole = ({
  columnIndex,
  hasRankColumn,
}: {
  columnIndex: number;
  hasRankColumn: boolean;
}): ReportColumnRole => {
  if (hasRankColumn && columnIndex === 0) {
    return 'rank';
  }

  return columnIndex === (hasRankColumn ? 1 : 0) ? 'subject' : 'stat';
};

const SortableTable: React.FC<SortableTableProps> = ({
  columns,
  rows,
  variant,
}) => {
  const [sort, setSort] = useState<SortState | undefined>();
  const isReport = variant === 'report';
  const hasReportRankColumn = isReport && isRankColumn(columns[0]);
  const reportStatColumnCount = isReport
    ? Math.max(0, columns.length - (hasReportRankColumn ? 2 : 1))
    : 0;
  const reportMinWidthRem =
    REPORT_SUBJECT_MIN_WIDTH_REM +
    reportStatColumnCount * REPORT_STAT_COLUMN_WIDTH_REM +
    (hasReportRankColumn ? REPORT_RANK_COLUMN_WIDTH_REM : 0);
  const reportTableStyle: React.CSSProperties | undefined = isReport
    ? { minWidth: `${reportMinWidthRem}rem` }
    : undefined;

  const reportRoleForColumn = (
    columnIndex: number
  ): ReportColumnRole | undefined =>
    isReport
      ? getReportColumnRole({ columnIndex, hasRankColumn: hasReportRankColumn })
      : undefined;

  const reportColumnStyle = (
    columnIndex: number
  ): React.CSSProperties | undefined => {
    const role = reportRoleForColumn(columnIndex);

    if (role === 'rank') {
      return { width: `${REPORT_RANK_COLUMN_WIDTH_REM}rem` };
    }

    if (role === 'stat') {
      return { width: `${REPORT_STAT_COLUMN_WIDTH_REM}rem` };
    }

    return undefined;
  };

  const reportCellClassName = (columnIndex: number) => {
    const role = reportRoleForColumn(columnIndex);

    return cn(
      isReport && 'px-3',
      role === 'rank' && 'text-right whitespace-nowrap tabular-nums',
      role === 'stat' && 'text-right whitespace-nowrap tabular-nums',
      role === 'subject' && 'text-left whitespace-normal'
    );
  };

  const reportHeaderButtonClassName = (columnIndex: number) => {
    const role = reportRoleForColumn(columnIndex);

    return cn(
      'flex w-full items-center gap-2 hover:text-foreground',
      role === 'rank' && 'justify-end text-right',
      role === 'stat' && 'justify-end text-right',
      role === 'subject' && 'justify-start text-left',
      !role && 'text-left'
    );
  };

  const sortedRows = useMemo(() => {
    if (!sort) return rows;

    return [...rows].sort((a, b) =>
      compareValues(
        valueForSort(a[sort.columnIndex] ?? []),
        valueForSort(b[sort.columnIndex] ?? []),
        sort.direction
      )
    );
  }, [rows, sort]);

  const updateSort = (columnIndex: number) => {
    setSort((current) => {
      if (current?.columnIndex !== columnIndex) {
        return { columnIndex, direction: 'asc' };
      }

      return {
        columnIndex,
        direction: current.direction === 'asc' ? 'desc' : 'asc',
      };
    });
  };

  return (
    <Table
      className={cn(isReport && 'table-fixed text-xs sm:text-sm')}
      containerClassName="rounded-lg border border-border"
      style={reportTableStyle}
    >
      {isReport && (
        <colgroup>
          {columns.map((_, columnIndex) => (
            <col
              key={`col-${columnIndex}`}
              style={reportColumnStyle(columnIndex)}
            />
          ))}
        </colgroup>
      )}
      <TableHeader>
        <TableRow>
          {columns.map((column, columnIndex) => {
            const isSorted = sort?.columnIndex === columnIndex;
            const Icon = isSorted
              ? sort.direction === 'asc'
                ? ArrowUpIcon
                : ArrowDownIcon
              : ArrowUpDownIcon;

            return (
              <TableHead
                key={`head-${columnIndex}`}
                className={reportCellClassName(columnIndex)}
              >
                <button
                  type="button"
                  className={reportHeaderButtonClassName(columnIndex)}
                  onClick={() => updateSort(columnIndex)}
                >
                  <span
                    className={cn(
                      reportRoleForColumn(columnIndex) === 'rank' &&
                        'text-right',
                      reportRoleForColumn(columnIndex) === 'stat' &&
                        'text-right'
                    )}
                  >
                    <RichTextRenderer nodes={column} />
                  </span>
                  <Icon className="size-3 shrink-0" aria-hidden="true" />
                </button>
              </TableHead>
            );
          })}
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedRows.map((row, rowIndex) => (
          <TableRow key={`row-${rowIndex}`}>
            {row.map((cell, cellIndex) => (
              <TableCell
                key={`cell-${rowIndex}-${cellIndex}`}
                className={reportCellClassName(cellIndex)}
              >
                <RichTextRenderer nodes={cell} />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default SortableTable;
